import { coerceCostSummary, coerceUsageSummary } from '@/lib/ai/costs';
import { isSummaryLengthOption, type SummaryLengthOption } from '@/lib/link-preview/summary-options';
import { toBooleanIfBoolean, toNumberOrNull } from '@/lib/utils/type-coercion';
import { toTrimmedNonEmptyString } from '@/lib/utils/type-guards';
import type { AICostSummary, AIUsageSummary } from '@/types/ai';

// Shared metadata shape emitted by every summarize stream (tweet, conversation, link, etc.).
export type SummaryModeSetting = 'summary' | 'explain' | 'custom';

export interface SummaryStreamMetadata {
  availableImages: number | null;
  consideredTweets: number | null;
  contentBudgetCharacters: number | null;
  contentTruncated: boolean | null;
  cost: AICostSummary | null;
  customPrompt: string | null;
  finishReason: string | null;
  includedImages: number | null;
  includedLinks: number | null;
  mode: SummaryModeSetting | null;
  requestedModel: string | null;
  requestedSearchMode: 'auto' | 'off' | null;
  resolvedModel: string | null;
  responseTimeMs: number | null;
  searchMode: 'auto' | 'off' | null;
  shouldIncludeImages: boolean | null;
  sourceCharacters: number | null;
  statusMessage: string | null;
  summaryCharacters: number | null;
  summaryLength: SummaryLengthOption | null;
  tokenHint: number | null;
  totalTokens: number | null;
  transcriptAvailable: boolean | null;
  transcriptCharacters: number | null;
  transcriptLines: number | null;
  transcriptMessage: string | null;
  transcriptSource: string | null;
  truncated: boolean | null;
  usage: AIUsageSummary | null;
  videoContentType: string | null;
  videoDurationSeconds: number | null;
  videoPipelinePhase: string | null;
  videoPosterUrl: string | null;
  videoSizeBytes: number | null;
  videoSource: string | null;
  videoTitle: string | null;
}

export const DEFAULT_SUMMARY_STREAM_METADATA: SummaryStreamMetadata = {
  summaryLength: null,
  requestedModel: null,
  resolvedModel: null,
  tokenHint: null,
  includedImages: null,
  availableImages: null,
  shouldIncludeImages: null,
  customPrompt: null,
  mode: null,
  searchMode: null,
  requestedSearchMode: null,
  finishReason: null,
  totalTokens: null,
  truncated: null,
  contentTruncated: null,
  contentBudgetCharacters: null,
  usage: null,
  cost: null,
  responseTimeMs: null,
  sourceCharacters: null,
  summaryCharacters: null,
  transcriptAvailable: null,
  transcriptSource: null,
  transcriptMessage: null,
  transcriptCharacters: null,
  transcriptLines: null,
  consideredTweets: null,
  includedLinks: null,
  videoDurationSeconds: null,
  videoSizeBytes: null,
  videoContentType: null,
  videoSource: null,
  videoTitle: null,
  videoPosterUrl: null,
  videoPipelinePhase: null,
  statusMessage: null,
};

type JsonRecord = Record<string, unknown>;

type SummaryMetadataPayload = Partial<SummaryStreamMetadata> & {
  model?: unknown;
  phase?: unknown;
  message?: unknown;
  characters?: unknown;
};

const isSearchMode = (value: string): value is 'auto' | 'off' => value === 'auto' || value === 'off';

const isSummaryModeSetting = (value: string): value is SummaryModeSetting =>
  value === 'summary' || value === 'explain' || value === 'custom';

const toFiniteNumber = (value: unknown): number | null => toNumberOrNull(value);

const toBoolean = (value: unknown): boolean | null => {
  const bool = toBooleanIfBoolean(value);
  return bool ?? null;
};

const toStringOrNull = (value: unknown): string | null => toTrimmedNonEmptyString(value);

const toSummaryLength = (value: unknown): SummaryLengthOption | null => {
  if (isSummaryLengthOption(value)) {
    return value;
  }
  return null;
};

const toSearchMode = (value: unknown): 'auto' | 'off' | null => {
  const trimmed = toTrimmedNonEmptyString(value);
  if (trimmed && isSearchMode(trimmed)) {
    return trimmed;
  }
  return null;
};

const toSummaryMode = (value: unknown): SummaryModeSetting | null => {
  const trimmed = toTrimmedNonEmptyString(value);
  if (trimmed && isSummaryModeSetting(trimmed)) {
    return trimmed;
  }
  return null;
};

export const createSummaryMetadata = (seed?: Partial<SummaryStreamMetadata>): SummaryStreamMetadata => ({
  ...DEFAULT_SUMMARY_STREAM_METADATA,
  ...seed,
});

export function applySummaryStatus(metadata: SummaryStreamMetadata, payload: JsonRecord): SummaryStreamMetadata {
  const next: SummaryStreamMetadata = { ...metadata };
  const source = payload as SummaryMetadataPayload;

  const summaryLength = toSummaryLength(source.summaryLength);
  if (summaryLength) {
    next.summaryLength = summaryLength;
  }

  const requestedModel = toStringOrNull(source.requestedModel);
  if (requestedModel) {
    next.requestedModel = requestedModel;
  }

  const resolvedModel = toStringOrNull(source.model);
  if (resolvedModel) {
    next.resolvedModel = resolvedModel;
  }

  const tokenHint = toFiniteNumber(source.tokenHint);
  if (tokenHint !== null) {
    next.tokenHint = Math.max(0, Math.trunc(tokenHint));
  }

  const includedImages = toFiniteNumber(source.includedImages);
  if (includedImages !== null) {
    next.includedImages = Math.max(0, Math.trunc(includedImages));
    if (next.shouldIncludeImages === null) {
      next.shouldIncludeImages = includedImages > 0;
    }
  }

  const maybeMode = toSummaryMode(source.mode);
  if (maybeMode) {
    next.mode = maybeMode;
  }

  const requestedSearchMode = toSearchMode(source.requestedSearchMode);
  if (requestedSearchMode) {
    next.requestedSearchMode = requestedSearchMode;
  }

  const searchMode = toSearchMode(source.searchMode);
  if (searchMode) {
    next.searchMode = searchMode;
  }

  const customPrompt = source.customPrompt === null ? null : toStringOrNull(source.customPrompt);
  if (customPrompt !== undefined) {
    next.customPrompt = customPrompt;
  }

  const consideredTweets = toFiniteNumber(source.consideredTweets);
  if (consideredTweets !== null) {
    next.consideredTweets = Math.max(0, Math.trunc(consideredTweets));
  }

  const includedLinks = toFiniteNumber(source.includedLinks);
  if (includedLinks !== null) {
    next.includedLinks = Math.max(0, Math.trunc(includedLinks));
  }

  // Video enrichments flow through the same summary metadata contract so links/tweets stay in sync.
  const videoDurationSeconds = toFiniteNumber(source.videoDurationSeconds);
  if (videoDurationSeconds !== null) {
    next.videoDurationSeconds = Math.max(0, videoDurationSeconds);
  }

  const videoSizeBytes = toFiniteNumber(source.videoSizeBytes);
  if (videoSizeBytes !== null) {
    next.videoSizeBytes = Math.max(0, Math.trunc(videoSizeBytes));
  }

  const videoContentType = toStringOrNull(source.videoContentType);
  if (videoContentType !== null) {
    next.videoContentType = videoContentType;
  }

  const videoSource = toStringOrNull(source.videoSource);
  if (videoSource !== null) {
    next.videoSource = videoSource;
  }

  const videoTitle = toStringOrNull(source.videoTitle);
  if (videoTitle !== null) {
    next.videoTitle = videoTitle;
  }

  const videoPosterUrl = toStringOrNull(source.videoPosterUrl);
  if (videoPosterUrl !== null) {
    next.videoPosterUrl = videoPosterUrl;
  }

  if (Object.hasOwn(source, 'phase')) {
    const phaseValue = toStringOrNull(source.phase);
    next.videoPipelinePhase = phaseValue;
  }

  if (Object.hasOwn(source, 'message')) {
    const statusMessage = toStringOrNull(source.message);
    next.statusMessage = statusMessage;
  }

  const phase = toStringOrNull(source.phase);
  const characters = toFiniteNumber(source.characters);
  if (characters !== null && phase === 'started') {
    next.sourceCharacters = Math.max(0, Math.trunc(characters));
  }

  const transcriptAvailable = toBoolean(source.transcriptAvailable);
  if (transcriptAvailable !== null) {
    next.transcriptAvailable = transcriptAvailable;
  }

  const transcriptSource = toStringOrNull(source.transcriptSource);
  if (transcriptSource !== null) {
    next.transcriptSource = transcriptSource;
  }

  const transcriptMessage = toStringOrNull(source.transcriptMessage);
  if (transcriptMessage !== null) {
    next.transcriptMessage = transcriptMessage;
  }

  const transcriptCharacters = toFiniteNumber(source.transcriptCharacters);
  if (transcriptCharacters !== null) {
    next.transcriptCharacters = Math.max(0, Math.trunc(transcriptCharacters));
  }

  const transcriptLines = toFiniteNumber(source.transcriptLines);
  if (transcriptLines !== null) {
    next.transcriptLines = Math.max(0, Math.trunc(transcriptLines));
  }

  return next;
}

export function applySummaryCompletion(metadata: SummaryStreamMetadata, payload: JsonRecord): SummaryStreamMetadata {
  const next: SummaryStreamMetadata = { ...metadata };
  const source = payload as SummaryMetadataPayload;

  const summaryLength = toSummaryLength(source.summaryLength);
  if (summaryLength) {
    next.summaryLength = summaryLength;
  }

  const requestedModel = toStringOrNull(source.requestedModel);
  if (requestedModel) {
    next.requestedModel = requestedModel;
  }

  const resolvedModel = toStringOrNull(source.model);
  if (resolvedModel) {
    next.resolvedModel = resolvedModel;
  }

  const includedImages = toFiniteNumber(source.includedImages);
  if (includedImages !== null) {
    next.includedImages = Math.max(0, Math.trunc(includedImages));
    if (next.shouldIncludeImages === null) {
      next.shouldIncludeImages = includedImages > 0;
    }
  }

  const searchMode = toSearchMode(source.searchMode);
  if (searchMode) {
    next.searchMode = searchMode;
  }

  const mode = toSummaryMode(source.mode);
  if (mode) {
    next.mode = mode;
  }

  const customPrompt = source.customPrompt === null ? null : toStringOrNull(source.customPrompt);
  if (customPrompt !== undefined) {
    next.customPrompt = customPrompt;
  }

  const finishReason = toStringOrNull(source.finishReason);
  if (finishReason) {
    next.finishReason = finishReason;
  }

  const totalTokens = toFiniteNumber(source.totalTokens);
  if (totalTokens !== null) {
    next.totalTokens = Math.max(0, Math.trunc(totalTokens));
  }

  const truncated = toBoolean(source.truncated);
  if (truncated !== null) {
    next.truncated = truncated;
  }

  const contentTruncated = toBoolean(source.contentTruncated);
  if (contentTruncated !== null) {
    next.contentTruncated = contentTruncated;
  }

  const contentBudgetCharacters = toFiniteNumber(source.contentBudgetCharacters);
  if (contentBudgetCharacters !== null) {
    next.contentBudgetCharacters = Math.max(0, Math.trunc(contentBudgetCharacters));
  }

  const responseTimeMs = toFiniteNumber(source.responseTimeMs);
  if (responseTimeMs !== null) {
    next.responseTimeMs = Math.max(0, Math.trunc(responseTimeMs));
  }

  const summaryCharacters = toFiniteNumber(source.characters);
  if (summaryCharacters !== null) {
    next.summaryCharacters = Math.max(0, Math.trunc(summaryCharacters));
  }

  const tokenHint = toFiniteNumber(source.tokenHint);
  if (tokenHint !== null) {
    next.tokenHint = Math.max(0, Math.trunc(tokenHint));
  }

  if (source.usage !== undefined) {
    next.usage = coerceUsageSummary(source.usage as Partial<AIUsageSummary> | null);
  }

  if (source.cost !== undefined) {
    next.cost = coerceCostSummary(source.cost as Partial<AICostSummary> | null);
  }

  if (!next.usage && typeof source.totalTokens === 'number' && Number.isFinite(source.totalTokens)) {
    const total = Math.max(0, Math.trunc(source.totalTokens));
    next.usage = { promptTokens: 0, completionTokens: 0, totalTokens: total };
  }

  // Carry video metadata across the completion payload so analytics + tooltips stay coherent.
  const videoDurationSeconds = toFiniteNumber(source.videoDurationSeconds);
  if (videoDurationSeconds !== null) {
    next.videoDurationSeconds = Math.max(0, videoDurationSeconds);
  }

  const videoSizeBytes = toFiniteNumber(source.videoSizeBytes);
  if (videoSizeBytes !== null) {
    next.videoSizeBytes = Math.max(0, Math.trunc(videoSizeBytes));
  }

  const videoContentType = toStringOrNull(source.videoContentType);
  if (videoContentType !== null) {
    next.videoContentType = videoContentType;
  }

  const videoSource = toStringOrNull(source.videoSource);
  if (videoSource !== null) {
    next.videoSource = videoSource;
  }

  const videoTitle = toStringOrNull(source.videoTitle);
  if (videoTitle !== null) {
    next.videoTitle = videoTitle;
  }

  const videoPosterUrl = toStringOrNull(source.videoPosterUrl);
  if (videoPosterUrl !== null) {
    next.videoPosterUrl = videoPosterUrl;
  }

  next.statusMessage = null;
  next.videoPipelinePhase = null;

  return next;
}
